import { useCallback, useEffect, useRef, useState } from "react";
import { uiBuildChanged } from "../lib/uiBuild";
import { WS_STATE_EVENT } from "../lib/websocket";
import { usePageVisible } from "./usePageVisible";

/**
 * Is the node that serves this UI still answering? (remote-node program N3
 * half 2, 2026-09-08.) A client — a phone, a laptop's browser, the desktop
 * app — keeps this page open while the node sleeps, reboots or drops off the
 * network; without a signal every action just fails one by one. This hook
 * probes `/api/health` on a slow cadence while things are fine (only while
 * the tab is visible), switches to a fast cadence once a probe fails, and
 * probes at once when the chat socket drops or the browser comes back online.
 * The banner that renders it says what to do (Rule 26); `probe` is its
 * "Try now".
 */
export const REACHABLE_INTERVAL_MS = 30_000;
export const UNREACHABLE_INTERVAL_MS = 5_000;
export const PROBE_TIMEOUT_MS = 6_000;

export interface NodeReachability {
	reachable: boolean;
	/** Plain words for the banner: "no answer", "no answer within 6 s", "answered 502". */
	detail: string;
	/** When the node stopped answering (ms epoch), null while reachable. */
	downSince: number | null;
	lastProbeAt: number | null;
	probing: boolean;
	probe: () => Promise<void>;
	/** The server serves a different UI build than this page runs (it was rebuilt under us) — say so, offer Reload. */
	staleBuild: boolean;
	/** The entry chunk the server serves, "" when it did not say. */
	servedEntry: string;
}

export function useNodeReachability(): NodeReachability {
	const visible = usePageVisible();
	const [reachable, setReachable] = useState(true);
	const [detail, setDetail] = useState("");
	const [downSince, setDownSince] = useState<number | null>(null);
	const [lastProbeAt, setLastProbeAt] = useState<number | null>(null);
	const [probing, setProbing] = useState(false);
	const [staleBuild, setStaleBuild] = useState(false);
	const [servedEntry, setServedEntry] = useState("");
	const inFlight = useRef<Promise<void> | null>(null);
	const reachableRef = useRef(true);

	const probe = useCallback((): Promise<void> => {
		if (inFlight.current) return inFlight.current;
		const run = (async () => {
			setProbing(true);
			const controller = new AbortController();
			const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
			let ok = false;
			let why = "";
			let served = "";
			try {
				const res = await fetch("/api/health", { cache: "no-store", credentials: "same-origin", signal: controller.signal });
				ok = res.ok;
				if (!ok) why = `answered ${res.status}`;
				else {
					try {
						const body = (await res.json()) as { ui_entry?: unknown };
						served = typeof body?.ui_entry === "string" ? body.ui_entry : "";
					} catch {
						served = "";
					}
				}
			} catch (err) {
				why = (err as Error)?.name === "AbortError" ? `no answer within ${Math.round(PROBE_TIMEOUT_MS / 1000)} s` : "no answer";
			} finally {
				clearTimeout(timer);
			}
			const now = Date.now();
			setLastProbeAt(now);
			setProbing(false);
			if (ok) {
				reachableRef.current = true;
				setReachable(true);
				setDetail("");
				setDownSince(null);
				setServedEntry(served);
				setStaleBuild(uiBuildChanged(served, window.location.hostname));
			} else {
				if (reachableRef.current) setDownSince(now);
				reachableRef.current = false;
				setReachable(false);
				setDetail(why);
			}
		})();
		inFlight.current = run;
		void run.finally(() => {
			inFlight.current = null;
		});
		return run;
	}, []);

	// The cadence: slow and visibility-gated while reachable, fast once down.
	useEffect(() => {
		if (reachable && !visible) return undefined;
		const every = reachable ? REACHABLE_INTERVAL_MS : UNREACHABLE_INTERVAL_MS;
		const id = window.setInterval(() => void probe(), every);
		return () => window.clearInterval(id);
	}, [reachable, visible, probe]);

	// Immediate probes: the tab comes back, the network comes back, the chat
	// socket drops (the first thing a person notices when the node is gone).
	useEffect(() => {
		if (visible && !reachableRef.current) void probe();
	}, [visible, probe]);
	useEffect(() => {
		const onOnline = () => void probe();
		const onWs = (ev: Event) => {
			const detail = (ev as CustomEvent<{ connected?: boolean }>).detail;
			if (detail && detail.connected === false) void probe();
		};
		window.addEventListener("online", onOnline);
		window.addEventListener(WS_STATE_EVENT, onWs);
		return () => {
			window.removeEventListener("online", onOnline);
			window.removeEventListener(WS_STATE_EVENT, onWs);
		};
	}, [probe]);

	return { reachable, detail, downSince, lastProbeAt, probing, probe, staleBuild, servedEntry };
}
